import React, { useState, useEffect } from 'react';
import { Button, Spinner, Alert as ReactAlert } from 'reactstrap';
import { IconButton, Stack, Tooltip, Dialog, DialogTitle, DialogContent, DialogActions, Box, TextField, InputAdornment } from '@mui/material';
import { Search as SearchIcon } from '@mui/icons-material';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { DateTimePicker } from '@mui/x-date-pickers/DateTimePicker';
import dayjs from 'dayjs';
import ru from 'dayjs/locale/ru';
import { del, put } from '../hooks/apiFetch';
import { AI_UTILITIES } from '../../constants/aiUtilities';
import './UserResults.css';
import { useNotification } from '../Notification/NotificationContext';

const PlannedResultsTab = ({
    plannedItems,
    setPlannedItems,
    loading,
    error,
    fetchPlanned,
    history,
    getFileIcon
}) => {
    const [userResults, setUserResults] = useState(plannedItems);
    const [filteredResults, setFilteredResults] = useState([]);
    const [deletingItems, setDeletingItems] = useState(new Set());
    const [searchTerm, setSearchTerm] = useState('');
    const [sortConfig, setSortConfig] = useState({ key: 'lastModificatedDate', direction: 'desc' });
    const {
        showSuccess,
        showError
    } = useNotification();

    const [isTimeModalOpen, setIsTimeModalOpen] = useState(false);
    const [editingTask, setEditingTask] = useState(null);
    const [selectedTime, setSelectedTime] = useState(null);
    const [isTimeSaving, setIsTimeSaving] = useState(false);

    const formatDateTimeWithLineBreak = (date) => {
        if (!date) return '';
        const dateObj = new Date(date);
        const formattedDate = dateObj.toLocaleDateString();
        const formattedTime = dateObj.toLocaleTimeString();
        return (
            <div className="date-time-container">
                <div className="date-part">{formattedDate}, {formattedTime}</div>
            </div>
        );
    };

    const renderFileList = (fileNames) => {
        if (!fileNames) return null;

        const filesArray = typeof fileNames === 'string'
            ? fileNames.split(', ').filter(name => name && name.trim())
            : Array.isArray(fileNames)
                ? fileNames.filter(name => name && name.trim())
                : [];

        if (filesArray.length === 0) return null;

        const CHARACTER_LIMIT = 30;
        const MAX_FULL_DISPLAY = 1;

        const totalChars = filesArray.reduce((sum, str) => sum + str.length, 0);
        const shouldTruncate = filesArray.length > MAX_FULL_DISPLAY ||
            totalChars > CHARACTER_LIMIT;

        return (
            <div className="file-list-container">
                {filesArray.map((fileName, index) => {
                    const extension = fileName.split('.').pop();
                    const nameWithoutExtension = fileName.slice(0, -(extension.length + 1));
                    const displayName = shouldTruncate
                        ? `${nameWithoutExtension.substring(0, 30)}…`
                        : fileName.trim();

                    return (
                        <div key={index} className="file-item">
                            <Tooltip title={fileName.trim()}>
                                <div className="file-item-content">
                                    {getFileIcon(fileName.trim())}
                                    <span className="file-name text-data full-file-name ml-1">
                                        {displayName}
                                    </span>
                                </div>
                            </Tooltip>
                        </div>
                    )
                })}
            </div>
        );
    };

    useEffect(() => {
        setUserResults(plannedItems);
    }, [plannedItems]);

    useEffect(() => {
        let results = userResults;

        if (searchTerm) {
            const lowercasedSearch = searchTerm.toLowerCase();
            results = userResults.filter(item =>
                (item.promptName && item.promptName.toString().toLowerCase().includes(lowercasedSearch)) ||
                (item.id && item.id.toString().toLowerCase().includes(lowercasedSearch)) ||
                item.utility.toLowerCase().includes(lowercasedSearch) ||
                (item.originalFileNames && item.originalFileNames.toString().toLowerCase().includes(lowercasedSearch))
            );
        }

        if (sortConfig.key) {
            results = [...results].sort((a, b) => {
                let aValue = a[sortConfig.key];
                let bValue = b[sortConfig.key];

                if (sortConfig.key === 'date' || sortConfig.key === 'plannedStartTime') {
                    aValue = new Date(aValue);
                    bValue = new Date(bValue);
                    aValue = aValue.getTime();
                    bValue = bValue.getTime();
                }

                if (typeof aValue === 'string') {
                    aValue = aValue.toLowerCase();
                    bValue = bValue.toLowerCase();
                }

                if (aValue < bValue) {
                    return sortConfig.direction === 'asc' ? -1 : 1;
                }
                if (aValue > bValue) {
                    return sortConfig.direction === 'asc' ? 1 : -1;
                }
                return 0;
            });
        }

        setFilteredResults(results);
    }, [userResults, searchTerm, sortConfig]);

    const fetchDeleteTask = async (inputIdentifier) => {
        await del(`/api/Task/DeleteTask/${encodeURIComponent(inputIdentifier)}`);
        showSuccess('Обработка успешно удалена');
    };

    const fetchUpdateTaskTime = async (inputIdentifier, newTime) => {
        await put(
            '/api/Task/UpdatePlannedTime',
            {
                identifier: inputIdentifier,
                plannedStartTime: newTime
            });
    };

    const handleDelete = async (inputIdentifier, role) => {
        let windowText;

        if (role === "Creator") {
            windowText = 'Вы уверены, что хотите отменить эту обработку?'
        } else {
            windowText = 'Вы уверены, что хотите покинуть команду? Эта обработка станет для вас недоступной'
        }

        if (!window.confirm(windowText)) {
            return;
        }

        setDeletingItems(prev => new Set(prev).add(inputIdentifier));

        try {
            if (role === "Creator") {
                await fetchDeleteTask(inputIdentifier);
            }

            setPlannedItems(prev => prev.filter(item => item.inputIdentifier !== inputIdentifier));
        } catch (error) {
            console.error('Ошибка при удалении:', error);
            showError(error.message || 'Произошла ошибка при удалении');
        } finally {
            setDeletingItems(prev => new Set([...prev].filter(identifier => identifier !== inputIdentifier)));
        }
    };

    const handleOpenTimeModal = (item) => {
        setEditingTask(item);
        // Инициализируем время. Если null, ставим текущее + 1 час для удобства
        const initialTime = item.plannedStartTime ? dayjs(item.plannedStartTime) : dayjs().add(1, 'hour');
        setSelectedTime(initialTime);
        setIsTimeModalOpen(true);
    };

    const handleCloseTimeModal = () => {
        setIsTimeModalOpen(false);
        setEditingTask(null);
        setSelectedTime(null);
    };

    const handleSaveTime = async () => {
        if (!editingTask || !selectedTime) return;

        setIsTimeSaving(true);
        try {
            await fetchUpdateTaskTime(editingTask.inputIdentifier, selectedTime);

            // Обновляем локальный стейт, чтобы не делать полный рефетч
            setUserResults(prev => prev.map(item =>
                item.inputIdentifier === editingTask.inputIdentifier
                    ? { ...item, plannedStartTime: selectedTime }
                    : item
            ));

            showSuccess('Время запуска обновлено');
            handleCloseTimeModal();
        } catch (error) {
            console.error('Ошибка при обновлении времени:', error);
            showError(error.message || 'Ошибка при сохранении времени');
        } finally {
            setIsTimeSaving(false);
        }
    };

    const handleRunNow = async () => {
        if (!editingTask) return;

        setIsTimeSaving(true);
        const now = dayjs();
        try {
            await fetchUpdateTaskTime(editingTask.inputIdentifier, now);

            setUserResults(prev => prev.map(item =>
                item.inputIdentifier === editingTask.inputIdentifier
                    ? { ...item, plannedStartTime: now }
                    : item
            ));

            showSuccess('Задача запущена немедленно');
            handleCloseTimeModal();
        } catch (error) {
            console.error('Ошибка при запуске:', error);
            showError(error.message || 'Ошибка при запуске задачи');
        } finally {
            setIsTimeSaving(false);
        }
    };

    const handleViewResults = (utility, inputIdentifier, event) => {
        const isMiddleClick = event.button === 1;
        const isCtrlClick = event.ctrlKey || event.metaKey;

        let path;

        switch (utility) {
            case AI_UTILITIES.TRANSCRIBE.name:
                path = `${AI_UTILITIES.TRANSCRIBE.path}/${inputIdentifier}`;
                break;
            case AI_UTILITIES.DIARIZE.name:
                path = `${AI_UTILITIES.DIARIZE.path}/${inputIdentifier}`;
                break;
            case AI_UTILITIES.SUMMARIZE_RESUME.name:
                path = `${AI_UTILITIES.SUMMARIZE_RESUME.path}/${inputIdentifier}`;
                break;
            case AI_UTILITIES.UNIVERSAL_SUMMARIZE.name:
                path = `${AI_UTILITIES.UNIVERSAL_SUMMARIZE.path}/${inputIdentifier}`;
                break;
            case AI_UTILITIES.DEPERSONALIZE.name:
                path = `${AI_UTILITIES.DEPERSONALIZE.path}/${inputIdentifier}`;
                break;
            case AI_UTILITIES.RETELLING.name:
                path = `${AI_UTILITIES.RETELLING.path}/${inputIdentifier}`;
                break;
            case AI_UTILITIES.STANDARD_CONTROL_PSA.name:
                path = `${AI_UTILITIES.STANDARD_CONTROL_PSA.path}/${inputIdentifier}`;
                break;
            case AI_UTILITIES.MARKET_PRICE_ANALYSIS.name:
                path = `${AI_UTILITIES.MARKET_PRICE_ANALYSIS.path}/${inputIdentifier}`;
                break;
            case AI_UTILITIES.INVOICE_RECOGNIZER.name:
                path = `${AI_UTILITIES.INVOICE_RECOGNIZER.path}/${inputIdentifier}`;
                break;
            default:
                path = '/myresults';
        }

        if (isMiddleClick || isCtrlClick) {
            event.preventDefault();
            const url = `${window.location.origin}${path}`;
            window.open(url, '_blank');
        } else {
            history.push(path);
        }
    };

    const handleSort = (key) => {
        let direction = 'asc';
        if (sortConfig.key === key && sortConfig.direction === 'asc') {
            direction = 'desc';
        }
        setSortConfig({ key, direction });
    };

    const getSortIcon = (key) => {
        if (sortConfig.key !== key) {
            return <span className="material-symbols-outlined sort-icon">unfold_more</span>;
        }
        return sortConfig.direction === 'asc'
            ? <span className="material-symbols-outlined sort-icon">arrow_upward</span>
            : <span className="material-symbols-outlined sort-icon">arrow_downward</span>;
    };

    const renderUserResultsTable = () => {
        return (
            <section className="table-responsive mt-0">
                <table className="table table-striped user-results-table">
                    <colgroup>
                        <col style={{ minWidth: '100px' }} />
                        <col style={{ minWidth: '100px' }} />
                        <col style={{ minWidth: '150px' }} />
                        <col style={{ minWidth: '100px' }} />
                        <col style={{ minWidth: '150px' }} />
                        <col style={{ minWidth: '80px' }} />
                        <col style={{ minWidth: '120px' }} />
                        <col style={{ minWidth: '120px' }} />
                        {filteredResults.some(item => item.comment) && (
                            <col style={{ minWidth: '150px' }} />
                        )}
                        <col style={{ minWidth: '120px' }} />
                    </colgroup>
                    <thead>
                        <tr>
                            <th
                                className="text-left align-middle sortable"
                                onClick={() => handleSort('id')}
                            >
                                <div className="header-content">
                                    <span className="text-data header-text">
                                        Id
                                    </span>
                                    <div className="sort-icon-container">
                                        {getSortIcon('id')}
                                    </div>
                                </div>
                            </th>
                            <th
                                className="text-left align-middle sortable"
                                onClick={() => handleSort('date')}
                            >
                                <div className="header-content">
                                    <span className="text-data header-text">
                                        Дата и время создания
                                    </span>
                                    <div className="sort-icon-container">
                                        {getSortIcon('date')}
                                    </div>
                                </div>
                            </th>
                            <th
                                className="text-left align-middle sortable"
                                onClick={() => handleSort('plannedStartTime')}
                            >
                                <div className="header-content">
                                    <span className="text-data header-text">
                                        Запланированное время старта
                                    </span>
                                    <div className="sort-icon-container">
                                        {getSortIcon('plannedStartTime')}
                                    </div>
                                </div>
                            </th>
                            <th
                                className="text-left align-middle sortable"
                                onClick={() => handleSort('utility')}
                            >
                                <div className="header-content">
                                    <span className="text-data header-text">
                                        Утилита
                                    </span>
                                    <div className="sort-icon-container">
                                        {getSortIcon('utility')}
                                    </div>
                                </div>
                            </th>
                            <th className="text-left align-middle">
                                <span className="text-data">
                                    Название промпта
                                </span>
                            </th>
                            <th className="text-left align-middle">
                                <span className="text-data">
                                    Владелец
                                </span>
                            </th>
                            <th className="text-left align-middle">
                                <span className="text-data">
                                    Ваша Роль
                                </span>
                            </th>
                            <th className="text-left align-middle">
                                <span className="text-data">
                                    Исходные Файлы
                                </span>
                            </th>
                            {filteredResults.some(item => item.comment) &&
                                <th className="text-left align-middle">
                                    <span className="text-data">
                                        Комментарий
                                    </span>
                                </th>
                            }
                            <th className="text-left align-middle">
                                <span className="text-data">
                                    Действия
                                </span>
                            </th>
                        </tr>
                    </thead>
                    <tbody>
                        {filteredResults.map(item => (
                            <tr key={item.id}>
                                <td className="text-left align-middle">
                                    <span className="text-data">
                                        {item.id}
                                    </span>
                                </td>
                                <td className="text-left align-middle">
                                    <span className="text-data">
                                        {formatDateTimeWithLineBreak(item.date)}
                                    </span>
                                </td>
                                <td className="text-left align-middle">
                                    <span className="text-data">
                                        {formatDateTimeWithLineBreak(item.plannedStartTime)}
                                    </span>
                                </td>
                                <td className="text-left align-middle">
                                    <span className="text-data">
                                        {item.utility}
                                    </span>
                                </td>
                                <td className="text-left align-middle">
                                    <span className="text-data">
                                        {item.utility === "Универсальная суммаризация (техническая)"
                                            ? (item.promptName ? item.promptName : "Промпт был удалён")
                                            : "Стандартный промпт"
                                        }
                                    </span>
                                </td>
                                <td className="text-left align-middle">
                                    <span className="text-data">
                                        {item.creator}
                                    </span>
                                </td>
                                <td className="text-left align-middle">
                                    <span className="text-data">
                                        {item.roleDisplayName}
                                    </span>
                                </td>
                                <td className="text-left align-middle">
                                    {renderFileList(item.originalFileNames)}
                                </td>
                                {filteredResults.some(item => item.comment) &&
                                    <td className="text-left align-middle">
                                        <span className="text-data">
                                            {item.comment}
                                        </span>
                                    </td>
                                }
                                <td className="text-left align-middle">
                                    <Stack direction="row" spacing={1} alignItems="center">

                                        <Tooltip title="Изменить время старта">
                                            <IconButton
                                                onClick={(e) => handleOpenTimeModal(item)}
                                                size="small">
                                                <span class="material-symbols-outlined view-result-icon">
                                                    hourglass_top
                                                </span>
                                            </IconButton>
                                        </Tooltip>

                                        <Tooltip title="Перейти">
                                            <IconButton
                                                onClick={(e) => handleViewResults(item.utility, item.inputIdentifier, e)}
                                                onMouseDown={(e) => {
                                                    if (e.button === 1) {
                                                        e.preventDefault();
                                                        handleViewResults(item.utility, item.inputIdentifier, e);
                                                    }
                                                }}
                                                size="small">
                                                <span className="material-symbols-outlined view-result-icon">
                                                    arrow_circle_right
                                                </span>
                                            </IconButton>
                                        </Tooltip>

                                        <Tooltip title="Отменить">
                                            <IconButton
                                                onClick={() => handleDelete(item.inputIdentifier, item.role)}
                                                size="small"
                                                disabled={deletingItems.has(item.inputIdentifier)}
                                            >
                                                {deletingItems.has(item.inputIdentifier)
                                                    ? (
                                                        <span className="material-symbols-outlined icon delete-icon">
                                                            auto_delete
                                                        </span>
                                                    )
                                                    : (
                                                        <span className="material-symbols-outlined icon delete-icon">
                                                            delete
                                                        </span>
                                                    )}
                                            </IconButton>
                                        </Tooltip>
                                    </Stack>
                                </td>
                            </tr>
                        ))}
                    </tbody>
                </table>
            </section>
        );
    };

    return (
        <>
            <Dialog
                open={isTimeModalOpen}
                onClose={handleCloseTimeModal}
                maxWidth="sm"
                fullWidth
            >
                <DialogTitle>Управление временем запуска</DialogTitle>
                <DialogContent>
                    <Box sx={{ mt: 2, mb: 1 }}>
                        <LocalizationProvider dateAdapter={AdapterDayjs} adapterLocale={ru}>
                            <Stack spacing={3}>
                                <DateTimePicker
                                    className="datetime-picker"
                                    label="Время начала обработки"
                                    value={selectedTime}
                                    onChange={setSelectedTime}
                                    slotProps={{ textField: { fullWidth: true } }}
                                    ampm={false}
                                    format="DD.MM.YYYY HH:mm"
                                    disabled={isTimeSaving}
                                />
                            </Stack>
                        </LocalizationProvider>
                    </Box>
                </DialogContent>
                <DialogActions>
                    <Button onClick={handleCloseTimeModal} className="dt1520-button active-dt1520-button default-button view-button mt-10" disabled={isTimeSaving}>
                        Отмена
                    </Button>
                    <Button
                        onClick={handleRunNow}
                        className="dt1520-button active-dt1520-button blue-button-with-borders view-button mt-10"
                        disabled={isTimeSaving}
                    >
                        Запустить сейчас
                    </Button>
                    <Button
                        onClick={handleSaveTime}
                        className="dt1520-button active-dt1520-button blue-button-with-borders view-button mt-10"
                        disabled={isTimeSaving || !selectedTime}
                    >
                        Сохранить время
                    </Button>
                </DialogActions>
            </Dialog>

            <div className="search-container">
                <TextField
                    fullWidth
                    variant="standard"
                    placeholder="Поиск по id, утилите, файлам, названию промпта..."
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    className="search-input text-input"
                    autoComplete="off"
                    InputProps={{
                        startAdornment: (
                            <InputAdornment position="start">
                                <SearchIcon className="search-icon" />
                            </InputAdornment>
                        ),
                    }}
                />
            </div>

            {loading ? (
                <div className="text-center my-5">
                    <Spinner color="primary" />
                    <p>Загрузка запланированных обработок...</p>
                </div>
            ) : error ? (
                <ReactAlert color="danger" transition={{ timeout: 300 }}>
                    <p className="alert-heading">Ошибка при загрузке данных:</p>
                    <p className="black-color">{error}</p>
                    <br />
                    <Button
                        className="dt1520-button active-dt1520-button default-button view-button mt-10"
                        onClick={fetchPlanned}>
                        Попробовать снова
                    </Button>
                </ReactAlert>
            ) : filteredResults.length > 0 ? (
                renderUserResultsTable()
            ) : userResults.length > 0 ? (
                <ReactAlert color="warning" className="mt-2" transition={{ timeout: 300 }}>
                    По вашему запросу ничего не найдено
                </ReactAlert>
            ) : (
                <ReactAlert color="info" className="mt-2" transition={{ timeout: 300 }}>
                    Нет данных о запланированных обработках
                </ReactAlert>
            )}
        </>
    );
};

export default PlannedResultsTab;